import Web3 from 'web3';
import axios from 'axios';

class HashSearchService {
  constructor(options = {}) {
    // Configuration
    this.environment = options.environment || 'development';
    this.web3 = null;
    
    // Ensure URL values are valid or use sensible defaults
    this.pinDataUrl = options.pinDataUrl || process.env.REACT_APP_PIN_DATA_URL || '/api/pins'; // Default to local endpoint
    this.ethereumRpcUrl = options.ethereumRpcUrl
    
    // Get max blocks to search (0 means no limit)
    this.maxBlocksToSearch = 0;
    
    // Active searches and callbacks
    this.activeSearches = new Map();
    this.callbacks = {};
    
    // Cache for pin data
    this.pinData = null;
    this.pinDataPromise = null;
    
    // Fix any URL values that might not have proper protocol
    this.fixUrls();
    
    // Log configuration in development
    if (this.environment === 'development') {
      console.log('[HashSearchService] Initialized with:');
      console.log('  • Pin Data URL:', this.pinDataUrl);
      console.log('  • Ethereum RPC URL:', this.ethereumRpcUrl);
      console.log('  • Max Blocks to Search:', this.maxBlocksToSearch);
    }
    
    // Initialize Web3
    this.initWeb3();
  }
  
  /**
   * Fix URLs to ensure they have proper protocols
   */
  fixUrls() {
    // Ensure pinDataUrl has a protocol or is relative
    if (this.pinDataUrl && !this.pinDataUrl.startsWith('/') && !this.pinDataUrl.includes('://')) {
      this.pinDataUrl = 'https://' + this.pinDataUrl;
    }
    
    // Ensure ethereumRpcUrl has a protocol
    if (this.ethereumRpcUrl && !this.ethereumRpcUrl.includes('://')) {
      this.ethereumRpcUrl = 'https://' + this.ethereumRpcUrl;
    }
  }
  
  /**
   * Initialize Web3 connection
   */
  initWeb3() {
    try {
      this.web3 = new Web3(this.ethereumRpcUrl);
    } catch (error) {
      console.error('[HashSearchService] Failed to initialize Web3:', error);
      throw new Error('Failed to initialize blockchain connection');
    }
  }
  
  /**
   * Fetch pin data from DynamoDB
   * @returns {Promise<Array>} Pin data
   */
  async fetchPinData() {
    // If we already have pin data, return it from cache
    if (this.pinData) {
      return this.pinData;
    }
    
    // If a fetch is in progress, wait for it
    if (this.pinDataPromise) {
      return this.pinDataPromise;
    }
    
    // Use empty array if pinDataUrl is not set
    if (!this.pinDataUrl) {
      console.warn('[HashSearchService] pinDataUrl is not set, using empty array for pin data');
      this.pinData = [];
      return [];
    }
    
    // Start a new fetch
    this.pinDataPromise = axios.get(this.pinDataUrl)
      .then(response => {
        try {
          // Safeguard against invalid response data
          if (!response || !response.data) {
            throw new Error('Invalid response data');
          }
          
          // Handle both array response and object with data property
          const rawData = Array.isArray(response.data) ? response.data : 
                         (response.data.data && Array.isArray(response.data.data)) ? response.data.data : null;
          
          if (!rawData) {
            throw new Error('Response does not contain array data');
          }
          
          // Sort pins by timestamp
          const data = rawData.sort((a, b) => {
            const timeA = a.Timedate || a.timedate || a.createdAt || 0;
            const timeB = b.Timedate || b.timedate || b.createdAt || 0;
            return new Date(timeB) - new Date(timeA);
          });
          
          this.pinData = data;
          return data;
        } catch (error) {
          console.error('[HashSearchService] Error processing pin data:', error.message);
          return [];
        }
      })
      .catch(error => {
        console.error('[HashSearchService] Error fetching pin data:', error);
        console.log('[HashSearchService] Using empty array for pin data as fallback');
        this.pinData = [];
        return [];
      })
      .finally(() => {
        // Clear the promise so we can retry on failure
        this.pinDataPromise = null;
      });
    
    return this.pinDataPromise;
  }
  
  /**
   * Search for a hash
   * @param {string} hash - Hash to search for
   * @param {string} timestamp - Optional timestamp to optimize search
   * @returns {Promise<Object>} Search result
   */
  async search(hash, timestamp = null) {
    // Generate a search ID
    const searchId = `search-${Date.now()}`;
    
    // Set up search context
    const searchContext = {
      id: searchId,
      hash: hash,
      timestamp: timestamp,
      isActive: true
    };
    
    this.activeSearches.set(searchId, searchContext);
    
    // Start the search process in the background
    setTimeout(() => this.doSearch(hash, timestamp, searchId), 0);
    
    return {
      success: true,
      searchId: searchId,
      message: 'Search started'
    };
  }
  
  /**
   * Perform the actual search
   * @param {string} hash - Hash to search for
   * @param {string} timestamp - Optional timestamp to optimize search
   * @param {string} searchId - Search ID
   */
  async doSearch(hash, timestamp, searchId) {
    try {
      // Normalize the hash
      const normalizedHash = hash.toLowerCase();
      
      // Emit search started event
      this.emitProgress(searchId, 'search_started', {
        message: 'Search started',
        percentComplete: 0
      });
      
      // Step 1: Check if the hash directly matches a pin
      this.emitProgress(searchId, 'progress', {
        message: 'Checking pin database...',
        percentComplete: 10
      });
      
      const pinData = await this.fetchPinData();
      
      // Find a direct match in the pin database
      const directMatch = pinData.find(pin => {
        return (pin.EthereumHash && pin.EthereumHash.toLowerCase() === normalizedHash) ||
          (pin.BitcoinHash && pin.BitcoinHash.toLowerCase() === normalizedHash) ||
          (pin.Pin && pin.Pin.toLowerCase() === normalizedHash);
      });
      
      if (directMatch) {
        // Return the raw DynamoDB data
        this.emitProgress(searchId, 'complete', {
          message: 'Hash found in pins database',
          percentComplete: 100,
          matchFound: true,
          data: {
            matchFound: true,
            matchDetails: directMatch
          }
        });
        
        return;
      }
      
      // Step 2: Find the closest pin to the timestamp
      this.emitProgress(searchId, 'progress', {
        message: 'Finding closest pin to timestamp...',
        percentComplete: 30
      });
      
      const closestPin = this.findClosestPin(pinData, timestamp);
      
      // Log detailed information about the closest pin (KEEP THIS LOGGING)
      if (closestPin) {
        console.log('[HashSearchService] Found closest pin details:');
        console.log('  • Timedate:', closestPin.Timedate);
        console.log('  • BlockNumber:', closestPin.BlockNumber || 'Not available');
        console.log('  • EthereumHash:', closestPin.EthereumHash || 'Not available');
        console.log('  • BitcoinHash:', closestPin.BitcoinHash || 'Not available');
        console.log('  • Pin:', closestPin.Pin || 'Not available');
        console.log('  • Id:', closestPin.Id || 'Not available');
      } else {
        console.log('[HashSearchService] No pins found, will search from most recent blocks');
      }
      
      // Step 3: Search the blockchain
      this.emitProgress(searchId, 'progress', {
        message: 'Preparing blockchain search...',
        percentComplete: 40
      });
      
      // Get the latest block
      const latestBlock = await this.web3.eth.getBlockNumber();
      
      // Determine search range
      let startBlock = 0;
      let endBlock = latestBlock;
      let usedPinBlock = false;
      
      // If we have a closest pin, use its block number as the starting point
      if (closestPin) {
        // First, try to use the BlockNumber from the pin
        if (closestPin.BlockNumber) {
          const blockNumber = parseInt(closestPin.BlockNumber);
          if (!isNaN(blockNumber)) {
            // Valid block number found in the pin record
            startBlock = blockNumber;
            usedPinBlock = true;
            
            // If we have a limited search range, define the end block
            if (this.maxBlocksToSearch > 0) {
              // Search backward from the pin's block
              endBlock = startBlock;
              startBlock = Math.max(0, endBlock - this.maxBlocksToSearch + 1);
            } else {
              // For unlimited search, start from the pin's block and go backward to block 0
              endBlock = startBlock;
            }
          }
        }
        
        // If we couldn't use the BlockNumber, try the EthereumHash
        if (!usedPinBlock && closestPin.EthereumHash) {
          const ethereumHash = closestPin.EthereumHash.toLowerCase();
          
          try {
            // Try to find the block using this hash
            const blockByHash = await this.web3.eth.getBlock(ethereumHash);
            if (blockByHash) {
              const blockNumber = parseInt(blockByHash.number);
              if (!isNaN(blockNumber)) {
                startBlock = blockNumber;
                usedPinBlock = true;
                
                // If we have a limited search range, define the end block
                if (this.maxBlocksToSearch > 0) {
                  // Search backward from the found block
                  endBlock = startBlock;
                  startBlock = Math.max(0, endBlock - this.maxBlocksToSearch + 1);
                } else {
                  // For unlimited search, start from the found block and go backward to block 0
                  endBlock = startBlock;
                }
              }
            }
          } catch (error) {
            console.log(`[HashSearchService] EthereumHash is not a valid block hash: ${error.message}`);
          }
        }
      }
      
      // If we couldn't determine a block from the pin, use the latest block
      if (!usedPinBlock) {
        // If we have a limited search, search most recent blocks
        if (this.maxBlocksToSearch > 0) {
          endBlock = latestBlock;
          startBlock = Math.max(0, endBlock - this.maxBlocksToSearch + 1);
        } else {
          // For unlimited search, search from the latest block backward
          endBlock = latestBlock;
        }
      }
      
      // Determine how many blocks to search
      let blocksToSearch;
      
      if (this.maxBlocksToSearch === 0) {
        // No limit - search all blocks from endBlock down to 0
        blocksToSearch = endBlock + 1; // +1 because we're including block 0
        startBlock = 0; // Ensure we search all the way to block 0
      } else {
        // Limited search - ensure we don't exceed the max
        blocksToSearch = Math.min(this.maxBlocksToSearch, endBlock - startBlock + 1);
        startBlock = endBlock - blocksToSearch + 1;
      }
      
      this.emitProgress(searchId, 'progress', {
        message: `Searching blockchain blocks...`,
        percentComplete: 50,
        currentBlock: startBlock,
        blocksProcessed: 0,
        totalBlocks: blocksToSearch
      });
      
      // Helper function for completing with a blockchain match
      const completeWithMatch = (matchDetails) => {
        // Log block information for the match we found
        console.log('[HashSearchService] Found match in blockchain:');
        console.log('  • Block Number:', matchDetails.blockNumber);
        console.log('  • Block Hash:', matchDetails.blockHash);
        console.log('  • Match Type:', matchDetails.matchType);
        if (matchDetails.transactionHash) {
          console.log('  • Transaction Hash:', matchDetails.transactionHash);
        }
        
        this.emitProgress(searchId, 'complete', {
          message: 'Hash found in blockchain',
          percentComplete: 100,
          matchFound: true,
          data: {
            matchFound: true,
            blockNumber: matchDetails.blockNumber,
            blockHash: matchDetails.blockHash,
            foundInInput: matchDetails.foundInInput || false,
            matchDetails: matchDetails,
            startingPin: closestPin
          }
        });
      };
      
      // Split the blocks into chunks for parallel processing
      const CHUNK_SIZE = 50;
      const CONCURRENT_CHUNKS = 12;
      const chunks = [];
      
      // Create chunks - searching from endBlock down to startBlock
      for (let blockNumber = endBlock; blockNumber >= startBlock; blockNumber -= CHUNK_SIZE) {
        const chunkEnd = blockNumber;
        const chunkStart = Math.max(startBlock, blockNumber - CHUNK_SIZE + 1);
        chunks.push({
          start: chunkStart,
          end: chunkEnd
        });
      }
      
      console.log(`[HashSearchService] Search divided into ${chunks.length} chunks of up to ${CHUNK_SIZE} blocks each`);
      
      // Track overall progress
      let blocksProcessed = 0;
      let searchCompleted = false;
      
      // Function to search a single block
      const searchBlock = async (blockNumber) => {
        try {
          const block = await this.web3.eth.getBlock(blockNumber, true);
          
          if (!block) {
            return null;
          }
          
          // Check if the hash matches the block hash
          if (block.hash.toLowerCase() === normalizedHash) {
            console.log(`[HashSearchService][Match] Hash found as block hash for block ${blockNumber}`);
            return {
              blockNumber: blockNumber,
              blockHash: block.hash,
              timestamp: new Date(block.timestamp * 1000).toISOString(),
              matchType: 'blockHash'
            };
          }
          
          // Check if the hash matches the parent hash
          if (block.parentHash.toLowerCase() === normalizedHash) {
            console.log(`[HashSearchService][Match] Hash found as parent hash for block ${blockNumber}`);
            return {
              blockNumber: blockNumber,
              blockHash: block.hash,
              timestamp: new Date(block.timestamp * 1000).toISOString(),
              matchType: 'parentHash'
            };
          }
          
          // Check transaction hashes and inputs
          if (block.transactions && block.transactions.length > 0) {
            for (let txIndex = 0; txIndex < block.transactions.length; txIndex++) {
              const tx = block.transactions[txIndex];
              
              if (typeof tx === 'object') {
                // Check transaction hash
                if (tx.hash.toLowerCase() === normalizedHash) {
                  console.log(`[HashSearchService][Match] Hash found as transaction hash in block ${blockNumber}, tx ${txIndex}`);
                  return {
                    blockNumber: blockNumber,
                    blockHash: block.hash,
                    timestamp: new Date(block.timestamp * 1000).toISOString(),
                    transactionHash: tx.hash,
                    matchType: 'transactionHash'
                  };
                }
                
                // Check transaction input for hash
                if (tx.input && tx.input.length > 2) {
                  const inputLower = tx.input.toLowerCase();
                  const hashWithoutPrefix = normalizedHash.slice(2); // Remove '0x' prefix
                  
                  // Check method 1: Direct substring match (existing approach)
                  if (inputLower.includes(hashWithoutPrefix)) {
                    console.log(`[HashSearchService][Match] Hash found in transaction input (direct) in block ${blockNumber}, tx ${txIndex}`);
                    return {
                      blockNumber: blockNumber,
                      blockHash: block.hash,
                      timestamp: new Date(block.timestamp * 1000).toISOString(),
                      transactionHash: tx.hash,
                      matchType: 'transactionInput',
                      foundInInput: true,
                      matchMethod: 'direct'
                    };
                  }
                  
                  // Check method 2: Look for function parameters
                  // In Ethereum transactions, parameters are often 32-byte aligned and padded
                  // This handles common parameter encoding patterns
                  
                  // Check for padded parameter (common for address and bytes32 parameters)
                  const paddedParam = '000000000000000000000000' + hashWithoutPrefix;
                  if (inputLower.includes(paddedParam)) {
                    console.log(`[HashSearchService][Match] Hash found in transaction input (padded) in block ${blockNumber}, tx ${txIndex}`);
                    return {
                      blockNumber: blockNumber,
                      blockHash: block.hash,
                      timestamp: new Date(block.timestamp * 1000).toISOString(),
                      transactionHash: tx.hash,
                      matchType: 'transactionInput',
                      foundInInput: true,
                      matchMethod: 'paddedParameter'
                    };
                  }
                  
                  // Check for trailing parameter (sometimes parameters are at the end with length encoding)
                  // Cut the hash into 8-word (16-character) segments since that's how they're often stored
                  if (hashWithoutPrefix.length >= 16) {
                    for (let i = 0; i <= hashWithoutPrefix.length - 16; i += 16) {
                      const hashSegment = hashWithoutPrefix.substring(i, i + 16);
                      if (inputLower.includes(hashSegment)) {
                        console.log(`[HashSearchService][Match] Hash segment found in transaction input in block ${blockNumber}, tx ${txIndex}, segment: ${hashSegment}`);
                        return {
                          blockNumber: blockNumber,
                          blockHash: block.hash,
                          timestamp: new Date(block.timestamp * 1000).toISOString(),
                          transactionHash: tx.hash,
                          matchType: 'transactionInput',
                          foundInInput: true,
                          matchMethod: 'segmentMatch',
                          matchedSegment: hashSegment
                        };
                      }
                    }
                  }
                }
                
                // Additionally, check receipt logs (common for event logs)
                try {
                  const receipt = await this.web3.eth.getTransactionReceipt(tx.hash);
                  if (receipt && receipt.logs && receipt.logs.length > 0) {
                    // Check each log entry
                    for (let logIndex = 0; logIndex < receipt.logs.length; logIndex++) {
                      const log = receipt.logs[logIndex];
                      
                      // Check topics (event signatures and indexed parameters)
                      if (log.topics && log.topics.length > 0) {
                        for (let topicIndex = 0; topicIndex < log.topics.length; topicIndex++) {
                          const topic = log.topics[topicIndex];
                          if (topic.toLowerCase() === normalizedHash) {
                            console.log(`[HashSearchService][Match] Hash found in event topic in block ${blockNumber}, tx ${txIndex}, log ${logIndex}, topic ${topicIndex}`);
                            return {
                              blockNumber: blockNumber,
                              blockHash: block.hash,
                              timestamp: new Date(block.timestamp * 1000).toISOString(),
                              transactionHash: tx.hash,
                              matchType: 'eventTopic',
                              logIndex: log.logIndex
                            };
                          }
                        }
                      }
                      
                      // Check log data
                      if (log.data && log.data.length > 2) {
                        const logData = log.data.toLowerCase();
                        const hashWithoutPrefix = normalizedHash.slice(2);
                        
                        if (logData.includes(hashWithoutPrefix)) {
                          console.log(`[HashSearchService][Match] Hash found in event data in block ${blockNumber}, tx ${txIndex}, log ${logIndex}`);
                          return {
                            blockNumber: blockNumber,
                            blockHash: block.hash,
                            timestamp: new Date(block.timestamp * 1000).toISOString(),
                            transactionHash: tx.hash,
                            matchType: 'eventData',
                            logIndex: log.logIndex
                          };
                        }
                      }
                    }
                  }
                } catch (receiptError) {
                  // Silent error handling
                }
              } else if (typeof tx === 'string' && tx.toLowerCase() === normalizedHash) {
                console.log(`[HashSearchService][Match] Hash found as transaction hash string in block ${blockNumber}, tx ${txIndex}`);
                return {
                  blockNumber: blockNumber,
                  blockHash: block.hash,
                  timestamp: new Date(block.timestamp * 1000).toISOString(),
                  transactionHash: tx,
                  matchType: 'transactionHash'
                };
              }
            }
          }
          
          return null; // No match in this block
        } catch (error) {
          // Silent error handling
          return null;
        }
      };
      
      // Function to process a chunk
      const processChunk = async (chunk) => {
        if (searchCompleted) return null;
        
        // Log which blocks we're checking in this chunk
        console.log(`[HashSearchService][Chunk] Processing blocks ${chunk.end} to ${chunk.start}`);
        
        for (let blockNumber = chunk.end; blockNumber >= chunk.start; blockNumber--) {
          // Double-check if the search is already completed before checking this block
          if (searchCompleted || (!this.activeSearches.has(searchId) || !this.activeSearches.get(searchId).isActive)) {
            return null;
          }
                    
          const match = await searchBlock(blockNumber);
          
          blocksProcessed++;
          
          // Update progress periodically
          if (blocksProcessed % 5 === 0 || blocksProcessed === blocksToSearch) {
            const percentComplete = 50 + Math.min(49, Math.floor((blocksProcessed / blocksToSearch) * 50));
            this.emitProgress(searchId, 'progress', {
              message: `Checking block ${blockNumber}...`,
              percentComplete,
              currentBlock: blockNumber,
              blocksProcessed,
              totalBlocks: blocksToSearch
            });
          }
          
          if (match) {
            console.log(`[HashSearchService][Match] Found match in block ${blockNumber}!`);
            // Mark as completed immediately to prevent other chunks from continuing
            searchCompleted = true;
            return match;
          }
        }
        
        return null;
      };
      
      try {
        // Process chunks in parallel with a concurrency limit
        for (let i = 0; i < chunks.length && !searchCompleted; i += CONCURRENT_CHUNKS) {
          // Exit early if the search has been cancelled
          if (!this.activeSearches.has(searchId) || (!this.activeSearches.get(searchId).isActive)) {
            console.log(`[HashSearchService] Search ${searchId} was cancelled`);
            return;
          }
          
          const currentChunks = chunks.slice(i, i + CONCURRENT_CHUNKS);
          
          this.emitProgress(searchId, 'processing_chunks', {
            message: `Processing batch ${Math.floor(i / CONCURRENT_CHUNKS) + 1} of ${Math.ceil(chunks.length / CONCURRENT_CHUNKS)}`,
            currentBatch: Math.floor(i / CONCURRENT_CHUNKS) + 1,
            totalBatches: Math.ceil(chunks.length / CONCURRENT_CHUNKS),
            percentComplete: ((blocksProcessed / blocksToSearch) * 50) + 50
          });
          
          console.log(`[HashSearchService][Batch] Processing batch ${Math.floor(i / CONCURRENT_CHUNKS) + 1} of ${Math.ceil(chunks.length / CONCURRENT_CHUNKS)}`);
          
          // Process chunks in parallel
          const results = await Promise.all(currentChunks.map(processChunk));
          
          // Check if any match was found
          const match = results.find(result => result !== null);
          
          if (match) {
            searchCompleted = true;
            // No need to set matchFound as it's never used elsewhere
            console.log(`[HashSearchService][Match] Found match - completing search`);
            completeWithMatch(match);
            return;
          }
          
          // Double-check if search has been cancelled or completed
          if (searchCompleted || (!this.activeSearches.has(searchId) || !this.activeSearches.get(searchId).isActive)) {
            console.log(`[HashSearchService] Search stopped after batch ${Math.floor(i / CONCURRENT_CHUNKS) + 1}`);
            return;
          }
        }
        
        // If we got here, no match was found
        if (!searchCompleted) {
          this.emitProgress(searchId, 'complete', {
            message: 'Search completed, no exact matches found',
            percentComplete: 100,
            matchFound: false,
            data: {
              matchFound: false,
              startingPin: closestPin
            }
          });
        }
      } catch (error) {
        if (!searchCompleted) {
          this.emitProgress(searchId, 'error', {
            message: `Error during search: ${error.message}`,
            error: error.message
          });
        }
      } finally {
        // Remove from active searches after a delay
        setTimeout(() => {
          this.activeSearches.delete(searchId);
        }, 3600000); // Keep in memory for 1 hour
      }
    } catch (error) {
      this.emitProgress(searchId, 'error', {
        message: `Error during search: ${error.message}`,
        error: error.message
      });
    }
  }
  
  /**
   * Find the earliest pin that happened AFTER the given timestamp
   * @param {Array} pinData - Array of pin data
   * @param {string} timestamp - Timestamp to search for
   * @returns {Object} Earliest pin that occurred after the timestamp
   */
  findClosestPin(pinData, timestamp) {
    if (!timestamp || !pinData || pinData.length === 0) {
      return pinData && pinData.length > 0 ? pinData[0] : null;
    }
    
    const targetTime = new Date(timestamp).getTime();
    
    // Filter pins that happened AFTER the target timestamp (pin time is LATER than target time)
    const pinsAfter = pinData.filter(pin => {
      const pinTime = new Date(pin.Timedate).getTime();
      return pinTime > targetTime; // Pin timestamp is LATER than target timestamp
    });
    
    if (pinsAfter.length === 0) {
      // If no pins after target, use the newest pin we have
      return pinData.sort((a, b) => new Date(b.Timedate) - new Date(a.Timedate))[0];
    }
    
    // Sort pins in ascending order by time (earliest first)
    const sortedPins = pinsAfter.sort((a, b) => {
      return new Date(a.Timedate).getTime() - new Date(b.Timedate).getTime();
    });
    
    // The first pin is the earliest one after the target timestamp
    const closestPin = sortedPins[0];
    
    return closestPin;
  }
  
  /**
   * Emit a progress event
   * @param {string} searchId - Search ID
   * @param {string} type - Event type
   * @param {Object} data - Event data
   */
  emitProgress(searchId, type, data) {
    if (!this.callbacks[searchId]) return;
    
    const event = {
      type,
      timestamp: new Date().toISOString(),
      ...data
    };
    
    this.callbacks[searchId](event);
  }
  
  /**
   * Get progress updates for a search
   * @param {string} searchId - Search ID
   * @param {function} callback - Callback function
   * @returns {function} Function to cancel updates
   */
  getProgressUpdates(searchId, callback) {
    if (!searchId) {
      throw new Error('Search ID is required');
    }
    
    this.callbacks[searchId] = callback;
    
    // Return a function to cancel the updates
    return () => {
      delete this.callbacks[searchId];
      
      // Also cancel the search if it's still active
      if (this.activeSearches.has(searchId)) {
        const search = this.activeSearches.get(searchId);
        search.isActive = false;
      }
    };
  }
}

export default HashSearchService; 